<script lang="ts">
  import type { Snippet } from 'svelte';
  import { trapFocus } from './trap-focus';

  interface Props {
    eyebrow: string;
    title: string;
    onClose: () => void;
    footer?: Snippet;
    children: Snippet;
  }

  let { eyebrow, title, onClose, footer, children }: Props = $props();

  function closeOnEscape(event: KeyboardEvent): void {
    if (event.key === 'Escape') onClose();
  }
</script>

<svelte:window onkeydown={closeOnEscape} />

<div class="backdrop" onclick={onClose} role="presentation"></div>
<div class="drawer" role="dialog" aria-modal="true" aria-label="{eyebrow}: {title}" use:trapFocus>
  <header class="drawer-header">
    <div>
      <span class="eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
    </div>
    <button class="close" onclick={onClose} aria-label="Fechar">✕</button>
  </header>
  <div class="drawer-body">{@render children()}</div>
  {#if footer}<footer class="drawer-footer">{@render footer()}</footer>{/if}
</div>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 100;
  }
  .drawer {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(480px, 100vw);
    background: var(--bg-elevated);
    border-left: 1px solid var(--border-subtle);
    box-shadow: var(--shadow-lg);
    z-index: 101;
    display: flex;
    flex-direction: column;
  }
  .drawer-header {
    padding: var(--space-5);
    border-bottom: 1px solid var(--border-subtle);
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }
  .eyebrow {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-tertiary);
  }
  h2 {
    margin: var(--space-1) 0 0;
    font-size: 16px;
    font-weight: 600;
  }
  .close {
    color: var(--text-tertiary);
    font-size: 18px;
    padding: var(--space-1) var(--space-2);
  }
  .close:hover {
    color: var(--text-primary);
  }
  .drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: var(--space-4) var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }
  .drawer-footer {
    padding: var(--space-4) var(--space-5);
    border-top: 1px solid var(--border-subtle);
    display: flex;
    justify-content: flex-end;
    gap: var(--space-2);
  }
</style>
